// The Floor's sessions, live (spec/09 §4, §6.3; F-STN-14, F-SES). A session has no channel of
// its own — the console learns a station's session changed from the live floor: `floor.station.updated`
// on `venue:{v}:floor` carries the station's mode (free, in_session, session_locked, …). While the
// organisation's socket is live, this subscribes to that channel and, for each station that changed,
// re-reads that station's session (`GET …/stations/{s}/session`) so the tiles, the List view and the
// station modal's free/in-session state reflect it at once. The modal's Session tab keeps its own poll for
// the ticking time left (there is no per-second push). The same event patches the cached floor snapshot, so a running
// session's now-playing line (F-CAT-04) follows what its station runs. On every (re)connect the station-session
// reads and the floor snapshot are refetched, so a change made while the socket was down converges (spec/09 §4:
// the snapshot is the recovery). When the socket is not live (reduced motion, or disconnected) the station-session
// lookups poll on their interval instead (`queries.ts`).

import { channels, FLOOR_STATION_UPDATED } from '@clavian/contracts/constants';
import type { FloorStationUpdatedPayload } from '@clavian/contracts/realtime';
import type { TypeId } from '@clavian/contracts/typeid';
import type { FloorSnapshot } from '@clavian/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { floorKeys } from '../../floor/queries';
import { applyFloorDelta } from '../../floor/realtime';
import { useChannelEvents, useRealtimeLive } from '../../realtime';
import { sessionKeys } from './queries';

/**
 * Keep the Floor's sessions live from the floor channel while the socket is live. Returns whether the
 * push is active, so the station-session lookups can drop their interval.
 */
export function useSessionsLive(organizationId: string, venueId: string): boolean {
  const queryClient = useQueryClient();
  const live = useRealtimeLive();

  // Snapshot-is-recovery (spec/09 §4): there is no server-side replay, so a session that started,
  // ended or locked while the socket was down leaves no frame to catch up on. When the socket comes
  // (back) up, resync — the station-session reads and the floor snapshot the now-playing line reads —
  // so the list converges instead of waiting on the next change. The station-session lookups poll off
  // while live (`queries.ts`), so without this a reconnect gap could stay stale indefinitely. This
  // mirrors the Orders, Kitchen and Calls live hooks, which already resync the same way.
  useEffect(() => {
    if (!live) return;
    void queryClient.invalidateQueries({ queryKey: sessionKeys.onStationAll(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, venueId) });
  }, [live, organizationId, venueId, queryClient]);

  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'floor'),
    (envelope) => {
      if (envelope.type !== FLOOR_STATION_UPDATED) return;
      const delta = envelope.p as FloorStationUpdatedPayload | undefined;
      const stationId = delta?.stationId;
      if (delta === undefined || typeof stationId !== 'string') return;
      // What the station is running now (F-CAT-04) rides the same event: patch the floor snapshot the
      // now-playing line reads, when it is cached. No snapshot yet means nothing to patch.
      queryClient.setQueryData<FloorSnapshot>(floorKeys.snapshot(organizationId, venueId), (snapshot) =>
        snapshot === undefined ? undefined : applyFloorDelta(snapshot, delta),
      );
      // The station's session may have started, ended or locked: re-read it (spec/09 §4 recovery).
      void queryClient.invalidateQueries({ queryKey: sessionKeys.onStation(organizationId, venueId, stationId) });
    },
    live,
  );

  return live;
}
